import React from "react";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  Bell,
  Building,
  Calendar,
  FileText,
  Heart,
  Pill,
  Link2,
  MapPin,
  Phone,
  Scale,
  ScanEye,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";

export type ParticipantSidebarItem = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

export const sidebarItems: ParticipantSidebarItem[] = [
  { id: "summary", label: "Summary", icon: FileText },
  { id: "missingitems", label: "Missing Items", icon: AlertTriangle },
  { id: "candidates", label: "Candidate List Entry", icon: Users },
  { id: "coordinatedcare", label: "Coordinated Care", icon: Calendar },
  { id: "outreach", label: "Client Outreach", icon: Phone },
  { id: "referencebrowser", label: "Reference Browser", icon: Link2 },
  { id: "documentscans", label: "Documents/Scans", icon: FileText },
  { id: "enrollment", label: "Enrollment", icon: Users },
  { id: "incidents", label: "Incidents", icon: AlertTriangle },
  { id: "legal", label: "Legal", icon: Scale },
  { id: "medical", label: "Medical", icon: Heart },
  { id: "medcall", label: "MedCall", icon: Pill },
  { id: "notes", label: "Notes", icon: FileText },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "disposition", label: "Plan and Location", icon: MapPin },
  { id: "programs", label: "Programs", icon: Building },
  { id: "progressreports", label: "Progress Reports", icon: FileText },
  { id: "scans", label: "Scans", icon: ScanEye },
];

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  items?: ParticipantSidebarItem[];
}

export const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange, items = sidebarItems }) => {
  return (
    <aside data-testid="participant-sidebar" className="app-page-shell flex h-full w-[17rem] flex-col overflow-hidden bg-[var(--surface-panel-solid)]">
      <div className="border-b border-[var(--neutral-gray-30)] px-3 py-3">
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--neutral-gray-80)]">
          Chart Sections
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-2.5 py-2.5">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;

          return (
            <Button
              key={item.id}
              id={`participant-sidebar-${item.id}-button`}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onTabChange(item.id)}
              className={cn(
                "h-auto w-full justify-start gap-2.5 rounded-lg px-2.5 py-2 text-left",
                isActive
                  ? "border border-[var(--azure-primary-border-soft)] bg-[var(--azure-primary-soft)] text-[var(--azure-primary)]"
                  : "border border-transparent text-[var(--neutral-gray-100)] hover:bg-[var(--surface-muted)] hover:text-[var(--neutral-gray-120)]",
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0",
                  isActive ? "text-[var(--azure-primary)]" : "text-[var(--neutral-gray-80)]",
                )}
              />
              <span className={cn("text-xs leading-4", isActive ? "font-semibold" : "font-medium")}>
                {item.label}
              </span>
            </Button>
          );
        })}
      </nav>
    </aside>
  );
};
